import { useMemo } from 'react';

import { FANTASY_API_VERSION, URLS } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';
import { League } from '@/types/api.generated';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { useQuery } from '@tanstack/react-query';

import { getFirstStartingEvent, useNextEventTime } from './utils';

type SocialFeedOptions = {
    enabled: boolean;
    league?: League;
    eventId?: string;
    playerId?: string;
};

export const useSocialFeedAndEventsData = ({ enabled, league, eventId, playerId }: SocialFeedOptions) => {
    const {
        data: socialFeed,
        isLoading: fetchingSocialFeed,
        refetch,
    } = useSocialFeed({ enabled, league, eventId, playerId });

    const filteredFeed = useFilteredSocialFeed(socialFeed);

    const fetching = !socialFeed?.length && fetchingSocialFeed;

    return {
        fetching,
        data: filteredFeed,
        refetch,
    };
};

const useSocialFeed = ({ enabled, league, eventId, playerId }: SocialFeedOptions) => {
    let queryKey: readonly string[] = socialFeedKeys.all;
    if (league) {
        queryKey = socialFeedKeys.league(league);
    }
    if (eventId) {
        queryKey = socialFeedKeys.event(eventId);
    }
    if (playerId) {
        queryKey = socialFeedKeys.player(playerId);
    }

    return useQuery({
        queryKey,
        queryFn: () => fetchSocialFeed({ league, eventId, playerId }),
        enabled,
    });
};

const socialFeedKeys = {
    all: ['social-feed'] as const,
    league: (league: string) => [...socialFeedKeys.all, league] as const,
    event: (id: string) => [...socialFeedKeys.all, id] as const,
    player: (id: string) => [...socialFeedKeys.all, 'player', id] as const,
};

/**
 * Filters out social feed entries whose first starting event has already started
 * relative to nextEventStartTime which only updates as events start.
 */
export const useFilteredSocialFeed = (socialFeed: SocialFeedEntry[] | undefined) => {
    const nextEventStartTime = useNextEventTime(socialFeed);
    return useMemo(() => {
        if (!socialFeed) return [];
        return socialFeed.filter(entry => {
            const firstEvent = getFirstStartingEvent(entry.picks);
            if (!firstEvent) return false;

            const eventTime = new Date(firstEvent.eventDate).getTime();

            // Keep entry only if the event hasn't started yet
            return eventTime > nextEventStartTime;
        });
    }, [socialFeed, nextEventStartTime]);
};

const fetchSocialFeed = async ({
    league,
    eventId,
    playerId,
}: {
    league?: League;
    eventId?: string;
    playerId?: string;
}): Promise<SocialFeedEntry[]> => {
    const url = new URL(`${URLS.FANTASY_REST_API_URL}/entry-feed/social-feed`);
    if (league) {
        url.searchParams.append('league', league);
    }
    if (eventId) {
        url.searchParams.append('eventId', eventId);
    }
    if (playerId) {
        url.searchParams.append('playerId', playerId);
    }
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: useUserStore.getState().session?.access_token ?? '',
            'Fantasy-Api-Version': FANTASY_API_VERSION,
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }

    return data;
};
